import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { useWindowListener } from '@quizzach/contexts';

interface Props {
	children?: ReactNode;
	closeModal?: () => void;
	visual?: boolean;
	explanation?: boolean;
	/**
	 * Controlled visibility. When the caller keeps the Modal mounted and
	 * toggles `show`, the exit animation plays before content unmounts.
	 * Defaults to true so `{open && <Modal>...}` call sites keep working
	 * (entrance animation only).
	 */
	show?: boolean;
}

// Body scroll lock shared across stacked modals.
let scrollLocks = 0;
const lockBodyScroll = () => {
	scrollLocks += 1;
	if (scrollLocks === 1) document.body.style.overflow = 'hidden';
};
const unlockBodyScroll = () => {
	scrollLocks = Math.max(0, scrollLocks - 1);
	if (scrollLocks === 0) document.body.style.overflow = '';
};

const FOCUSABLE =
	'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export const Modal = ({
	children,
	closeModal,
	visual,
	explanation,
	show = true,
}: Props) => {
	const elRef = useRef<HTMLElement | null>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const { touchDevice } = useWindowListener();
	const reducedMotion = useReducedMotion();

	// Store closeModal in ref to avoid effect re-runs when it changes
	const closeModalRef = useRef(closeModal);
	closeModalRef.current = closeModal;

	const containerElement = useMemo(() => {
		if (!elRef.current) {
			elRef.current = document.createElement('div');
		}
		return elRef.current;
	}, []);

	// Mount/unmount modal container
	useEffect(() => {
		const modalRoot = document.getElementById('modal');
		const element = containerElement;

		if (modalRoot && element) {
			modalRoot.appendChild(element);

			return () => {
				if (modalRoot.contains(element)) {
					modalRoot.removeChild(element);
				}
			};
		}
	}, [containerElement]);

	// Escape, focus trap + focus return, body scroll lock - active while shown.
	useEffect(() => {
		if (!show) return;

		const previouslyFocused = document.activeElement as HTMLElement | null;
		lockBodyScroll();
		// Focus the dialog itself so screen readers announce it and Tab
		// starts inside; rAF waits for the portal content to exist.
		requestAnimationFrame(() => panelRef.current?.focus());

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeModalRef.current?.();
				return;
			}
			if (event.key !== 'Tab' || !panelRef.current) return;

			const focusables = Array.from(
				panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
			).filter((el) => el.offsetParent !== null);
			if (focusables.length === 0) {
				event.preventDefault();
				return;
			}
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement;

			if (event.shiftKey && (active === first || active === panelRef.current)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			unlockBodyScroll();
			previouslyFocused?.focus?.();
		};
	}, [show]);

	const handleBackdropMouseDown = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (event.target === event.currentTarget) {
				closeModalRef.current?.();
			}
		},
		[]
	);

	// Standard dialogs become bottom sheets on touch layouts; the image
	// lightbox (visual) stays centered at every size.
	const sheet = touchDevice && !visual;

	const panelMotion = reducedMotion
		? {
				initial: { opacity: 0 },
				animate: { opacity: 1 },
				exit: { opacity: 0 },
				transition: { duration: 0.2 },
			}
		: sheet
			? {
					initial: { y: '100%' },
					animate: { y: 0 },
					exit: { y: '100%' },
					transition: { type: 'spring' as const, damping: 34, stiffness: 360 },
				}
			: {
					initial: { opacity: 0, y: 14, scale: 0.96 },
					animate: { opacity: 1, y: 0, scale: 1 },
					exit: { opacity: 0, y: 10, scale: 0.97 },
					transition: {
						duration: 0.28,
						ease: [0.2, 0.65, 0.2, 1] as const,
					},
				};

	const dragProps =
		sheet && closeModal && !reducedMotion
			? {
					drag: 'y' as const,
					dragConstraints: { top: 0, bottom: 0 },
					dragElastic: { top: 0, bottom: 0.6 },
					onDragEnd: (
						_e: unknown,
						info: { offset: { y: number }; velocity: { y: number } }
					) => {
						if (info.offset.y > 110 || info.velocity.y > 700) {
							closeModalRef.current?.();
						}
					},
				}
			: {};

	const panelClass = visual
		? 'modal-panel visual-container'
		: explanation
			? 'modal-panel explanation-container'
			: 'modal-panel modal-container';

	return createPortal(
		<AnimatePresence>
			{show && (
				<motion.div
					className={`modal-layer${sheet ? ' sheet' : ''}`}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.22 }}
					onMouseDown={handleBackdropMouseDown}
				>
					<motion.div
						ref={panelRef}
						className={panelClass}
						role='dialog'
						aria-modal='true'
						tabIndex={-1}
						{...panelMotion}
						{...dragProps}
					>
						{children}
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>,
		containerElement
	);
};
